import React from 'react';
import { Avatar, useTheme, Tooltip, Box } from '@mui/material';
import { AccountCircle as AccountCircleIcon } from '@mui/icons-material';

/**
 * Enhanced Avatar component with animation and styling
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.icon - Icon to display inside avatar
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alt text for image
 * @param {string} props.color - Background color (defaults to theme primary)
 * @param {Object} props.sx - Additional styles to apply
 * @param {string} props.tooltip - Optional tooltip text
 * @param {string} props.size - Size variant ('small', 'medium', 'large')
 * @param {string} props.variant - Avatar variant ('circular', 'rounded', 'square')
 * @param {Function} props.onClick - Click handler
 */
const EnhancedAvatar = ({
    icon,
    src,
    alt,
    color,
    sx = {},
    tooltip,
    size = 'medium',
    variant = 'circular',
    onClick,
    ...props
}) => {
    const theme = useTheme();

    // Size mappings
    const sizeMap = {
        small: { width: 28, height: 28, fontSize: '0.9rem' },
        medium: { width: 36, height: 36, fontSize: '1.1rem' },
        large: { width: 48, height: 48, fontSize: '1.3rem' },
    };

    // Color setup
    const bgColor = color || theme.palette.primary.main;
    const isTransparent = bgColor.includes('rgba');

    // Base styles
    const baseStyles = {
        ...sizeMap[size],
        bgcolor: isTransparent ? bgColor : `${bgColor}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
        },
        ...sx
    };

    // The avatar component
    const avatarComponent = (
        <Avatar
            src={src}
            alt={alt || 'Avatar'}
            variant={variant}
            onClick={onClick}
            sx={baseStyles}
            {...props}
        >
            {!src && (icon || <AccountCircleIcon />)}
        </Avatar>
    );

    // Wrap in tooltip if provided
    if (tooltip) {
        return (
            <Tooltip title={tooltip} arrow placement="top">
                <Box sx={{ display: 'inline-flex' }}>
                    {avatarComponent}
                </Box>
            </Tooltip>
        );
    }

    return avatarComponent;
};

export default EnhancedAvatar;